import { IkeToken } from '@ikegps/core-api';
import {
	type CollectionModel,
	type CollectionModelBase,
	type CollectionModelField,
	CollectionModelFieldAnchor,
	CollectionModelFieldFileTool,
	CollectionModelFieldImage,
	CollectionModelFieldLine,
	CollectionModelFieldNestedList,
	CollectionModelFieldSelectList,
	CollectionModelFieldVector,
	type FormModelField,
} from '@ikegps/core-shared';
import { FieldActionButton } from '../../components/button/field.action.button';
import { ImageButton } from '../../components/button/image.button';
import { ProFeatureTooltip } from '../../components/pro.feature.tooltip';
import { ModalService } from '../../components/modal/modal.service';
import { MaterialIcon } from '../../components/icon/icons';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { TopologicalEditorCollectionEditCtrl } from '../../page/topological.editor.page/topological.editor.collection.edit.ctrl';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { CollectionModelStateEdit } from '../../store/collection.edit.model';
import { classNames } from '../../util/classnames';
import type { ICollectionViewController } from './collection.view.ctrl.interface';
import { CSS } from './collection.view.style';
import { CollectionFieldViewBuilder } from './fields/field.view.builder';

export class CollectionEditViewField {
	static view(
		ctrl: ICollectionViewController,
		collection: CollectionModelBase,
		schema: FormModelField,
		showRedDot?: boolean
	): MithrilVNode {
		const id = `i-${collection.instanceId}-${schema.id}`;

		const field: CollectionModelField = collection.getField(schema.id);
		const fieldView = CollectionFieldViewBuilder.getFieldView(field);

		const hasValue = field.hasValue();

		const hasDetail = hasValue && fieldView.hasDetail && fieldView.hasDetail(field);
		// Keyed by field.id (like openLineVertex) so clearField can close it — not the composed DOM id.
		const showDetail = ctrl.isFieldDetailShown.get(field.id);

		const hasUpload = fieldView.canUpload && fieldView.canUpload(field) && ctrl.isEditing;
		let imageTool: CollectionModelFieldImage = null;
		if (fieldView.canUpload && fieldView.canUpload(field)) {
			imageTool = field as CollectionModelFieldImage;
		}

		const hasGeocoding =
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE) && fieldView.canGeocoding && fieldView.canGeocoding(field);

		const hasFile = fieldView.hasFile && fieldView.hasFile(field);
		let fileToolField: CollectionModelFieldFileTool = null;
		if (hasFile) {
			fileToolField = field as CollectionModelFieldFileTool;
		}

		const isLoading =
			(CollectionModelFieldFileTool.isFileToolField(field) || CollectionModelFieldImage.isImageField(field)) &&
			ctrl.isUploading &&
			ctrl.upload.fieldsInLoadingState.indexOf(field.id) > -1;

		let hasClear: boolean = false;

		// Without the line-tool token a line field can't be edited: edit mode shows its value area locked with a
		// Pro-only tooltip; read mode falls through to the normal read-only rendering (no draw button).
		const lineLocked = CollectionModelFieldLine.isLineField(field) && !Services.Token.has(IkeToken.FEATURE_LINE_TOOL);
		// Asset type fields (FEATURE_ASSET_TYPE gate) are deliberately NOT hard-locked: in a non-Pro department
		// they stay editable but the picker only offers the default "Collection" type (see AssetTypeFieldView),
		// so a required asset type can always be re-selected after clearing. Line tool has no such fallback, so
		// it stays Pro-locked. UFO-1733.
		const proLocked = lineLocked;

		let fieldValue: MithrilVNode;
		const userIsAdmin =
			Services.UserProfile.isDepartmentAdmin(Services.UserProfile.get(Services.User.current.objectId)) ||
			Services.UserProfile.isDepartmentDataAdmin(Services.UserProfile.get(Services.User.current.objectId));
		const userCanEditField = userIsAdmin || !field.schema.adminOnly;

		if (proLocked && ctrl.isEditing && userCanEditField) {
			// Only line fields reach this locked branch now (see proLocked above). Line has no empty-state edit
			// view — drawing starts from a dedicated button — so render the inline value or a disabled draw button.
			const lockedInner = hasValue
				? fieldView.viewInline(field, ctrl)
				: FieldActionButton.view({ icon: MaterialIcon.FIELD_LINE, text: T.FIELD_LINE_DRAW, disabled: true });
			fieldValue = ProFeatureTooltip.view({
				stretch: true,
				// Open downward: fields sit directly under the collection title bar, so an upward bubble is clipped by it.
				position: 'below',
				children: m('div', { className: CSS.field.value, style: { pointerEvents: 'none' } }, lockedInner),
			});
			// Only drawing is Pro-gated; clearing stays available. The clear button is a sibling of the
			// tooltip-wrapped value area, so the pointer-events lock doesn't reach it.
			hasClear = fieldView.hasClear == null ? hasValue : fieldView.hasClear(field);
		} else if (ctrl.isEditing && userCanEditField) {
			fieldValue = m('div', { className: CSS.field.value }, fieldView.viewEdit(field, ctrl));
			hasClear = fieldView.hasClear == null ? hasValue : fieldView.hasClear(field);
			// if the field is CollectionModelFieldVector and the provider is collection, but it's not exists anymore
			// the field text is 'Unknown' but we don't have value, so we don't show the clearButton
			// we fix that here
			if (
				(CollectionModelFieldVector.isVectorField(field) || CollectionModelFieldAnchor.isAnchorField(field)) &&
				typeof fieldView.viewEdit(field, ctrl) !== 'string' &&
				(fieldView.viewEdit(field, ctrl) as MithrilVNode).text === Translate(T.UNKNOWN)
			) {
				hasClear = true;
			}
		} else if (hasValue) {
			const inlineText = fieldView.viewInline(field, ctrl);

			if (typeof inlineText === 'string') {
				const children: (MithrilVNode | string)[] = [inlineText];

				if (
					// We have the correct token
					Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) &&
					// And the field is one of the two list types which can have colors
					(CollectionModelFieldSelectList.isSelectListField(field) ||
						CollectionModelFieldNestedList.isNestedListField(field)) &&
					// And this instance is the one that is marked as the color field
					field.collection.schema.metadata.mapColorFieldId === field.schema.id
				) {
					if (field.color) {
						children.push(m('div', { className: CSS.field.colorIndicator, style: { backgroundColor: field.color } }));
					}
				}

				fieldValue = m('div', { className: CSS.field.value, title: inlineText }, children);
			} else {
				fieldValue = m('div', { className: CSS.field.value }, inlineText);
			}
		}

		const title = schema.displayName;
		return m(
			'div',
			{
				className: classNames(CSS.field.container, {
					[CSS.isEmpty]: !hasValue,
				}),
				id,
				'data-testid': `field-${schema.id}`,
				onclick: (e) => {
					const handleClickAway = () => {
						CurrentState.setInspectedFieldId(null);

						Services.FocusAway.drop(e.target as HTMLElement, handleClickAway);
					};

					CurrentState.setInspectedFieldId(field.id);

					Services.FocusAway.from(e.target as HTMLElement, handleClickAway);
				},
			},
			[
				m('div', { className: CSS.field.summary }, [
					m('div', { className: CSS.field.title, title }, [
						title,
						showRedDot ? m('span', { className: CSS.field.redDot }) : null,
					]),
					fieldValue,
					isLoading ? CollectionEditViewField.viewLoadingSpinner(ctrl, field) : null,
					hasUpload ? CollectionEditViewField.viewFieldUploadButton(ctrl, imageTool) : null,
					hasFile ? CollectionEditViewField.viewFileDownloadButton(ctrl, fileToolField) : null,
					hasClear && hasFile ? CollectionEditViewField.viewFieldClearModalButton(ctrl, field) : null,
					hasClear && !hasFile ? CollectionEditViewField.viewFieldClearButton(ctrl, field, collection) : null,
					hasDetail ? CollectionEditViewField.viewFieldDetailButton(ctrl, field.id) : null,
					hasGeocoding ? CollectionEditViewField.viewFieldGeocodingButton(ctrl, field) : null,
					hasGeocoding && !ctrl.clearErrorDisabled(field)
						? CollectionEditViewField.viewFieldClearErrorButton(ctrl, field)
						: null,
				]),
				showDetail ? m('div', { className: CSS.field.detail }, fieldView.viewDetail(field, ctrl)) : null,
			]
		);
	}

	private static viewFieldClearErrorButton(ctrl: ICollectionViewController, field: CollectionModelField) {
		return ImageButton.view(MaterialIcon.CLOSE, {
			onclick: () => ctrl.clearError(field),
			disabled: ctrl.clearErrorDisabled(field),
			testId: 'field-clear-error',
		});
	}

	private static viewFieldClearButton(
		ctrl: ICollectionViewController,
		field: CollectionModelField,
		collection: CollectionModelBase
	) {
		return ImageButton.view(MaterialIcon.CLOSE, {
			onclick: async () => {
				const handled = await CollectionEditViewField.handleSpanFieldClear(field, ctrl, collection.collection);
				if (!handled) {
					ctrl.clearField(field);
				}
			},
			testId: 'field-clear',
		});
	}

	/**
	 * Handle clearing a span vector field with two-way link detection.
	 * If the field is a span vector with a reciprocal link, shows a confirmation
	 * dialog and clears both sides. Returns false for standard clear fallthrough.
	 */
	private static async handleSpanFieldClear(
		field: CollectionModelField,
		ctrl: ICollectionViewController,
		collection: CollectionModel
	): Promise<boolean> {
		if (!CollectionModelFieldVector.isVectorField(field)) {
			return false;
		}
		if (!collection || !field.isPartOfSpanSubForm() || !field.parent.schema.spanValidator.hasCompleteState) {
			return false;
		}
		if (!field.isCollectionLink()) {
			return false;
		}

		let reciprocal: Awaited<ReturnType<typeof Services.SubFormLink.findReciprocalLink>>;
		try {
			reciprocal = await Services.SubFormLink.findReciprocalLink(field, collection);
		} catch {
			return false;
		}
		if (!reciprocal) {
			return false;
		}

		const targetTitle = reciprocal.targetCollection.title || 'linked collection';
		const confirmed = await ModalService.open({
			title: T.SPAN_UNLINK_CONFIRM_TITLE,
			content: Translate(T.SPAN_UNLINK_CONFIRM_MESSAGE, { targetTitle }),
			hasCancel: true,
			okName: T.SPAN_UNLINK_CONFIRM_OK,
		});

		if (!confirmed.ok) {
			return true;
		}

		// Clear source vector field
		field.Mutator.clear();

		// Check if this is an unsaved two-way link that can be fully reverted.
		// Only do this on the map page — the topo editor manages each pole's edit
		// state independently via its own edit controllers.
		const isMapPage = !TopologicalEditorCollectionEditCtrl.is(ctrl);
		const isUnsavedLink =
			isMapPage && CurrentState.CollectionLink.modifiedTargetCollections.has(reciprocal.targetCollection.id);

		if (isUnsavedLink) {
			// Unsaved link: revert the target entirely. performTwoWaySpanLink copies
			// span instances, transforms fields, and sets vectors — finishEdit reverts all of it.
			CurrentState.CollectionLink.removeModifiedTarget(reciprocal.targetCollection.id);
		} else {
			// Previously saved link, or topo editor — clear target vector and track for save
			if (!reciprocal.targetCollection.isEditing) {
				if (TopologicalEditorCollectionEditCtrl.is(ctrl)) {
					ctrl.registerCollectionForEditing(reciprocal.targetCollection);
				} else {
					reciprocal.targetCollection.beginEdit((state) => new CollectionModelStateEdit(state));
				}
			}
			reciprocal.targetVectorField.Mutator.clear();

			CurrentState.CollectionLink.registerModifiedTarget(reciprocal.targetCollection, collection.id);
		}

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			'span:unlink:two-way',
			field.schema.displayName,
			1
		);

		NotificationService.info({
			key: T.SPAN_UNLINK_BOTH_SIDES,
			attrs: { targetTitle },
		});

		return true;
	}

	private static viewFieldClearModalButton(ctrl: ICollectionViewController, field: CollectionModelField) {
		return ImageButton.view(MaterialIcon.CLOSE, {
			onclick: () => ctrl.clearModalField(field),
			testId: 'field-clear',
		});
	}

	private static viewFieldDetailButton(ctrl: ICollectionViewController, fieldInstanceId: string) {
		let icon: MaterialIcon = MaterialIcon.INFO_FILLED;
		if (ctrl.isFieldDetailShown.get(fieldInstanceId)) {
			icon = MaterialIcon.INFO;
		}

		return ImageButton.view(icon, {
			onclick: () => ctrl.toggleFieldDetail(fieldInstanceId),
			testId: 'field-detail',
		});
	}

	private static viewFieldGeocodingButton(ctrl: ICollectionViewController, field: CollectionModelField) {
		return ImageButton.view(MaterialIcon.LOCATION, {
			onclick: () => ctrl.fillAddress(field),
			disabled: ctrl.fillAddressDisabled(field),
			title: ctrl.geocodingButtonTitle(field),
			testId: 'field-geocode',
		});
	}

	private static viewLoadingSpinner(ctrl: ICollectionViewController, field: CollectionModelField) {
		return Spinner.view();
	}

	private static viewFileDownloadButton(ctrl: ICollectionViewController, field: CollectionModelFieldFileTool) {
		return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
			onclick: (e) => ctrl.upload.fileDownload(field),
			testId: 'field-download',
		});
	}

	private static viewFieldUploadButton(ctrl: ICollectionViewController, field: CollectionModelFieldImage) {
		return m(
			'div',
			{ className: CSS.field.isImageUpload },
			m(
				'div',
				{
					id: `image-upload-${field.id}`,
					style: 'border:none',
					className: CSS.container,
					'data-testid': 'field-upload',
					onclick: (e) => ctrl.upload.openImageUpload(e, field),
				},
				ImageButton.view(MaterialIcon.FILE_UPLOAD)
			)
		);
	}
}
